// Both engines keep their files in Emscripten IDBFS mounts: an IndexedDB
// database named after the mount point, with every path as a key in
// FILE_DATA. Reading one directly lists games and saves without booting an
// emulator core (a core owns the canvas and audio, one per page).

export interface StoredFile {
  path: string;
  /** The file name, without its directory. */
  name: string;
  bytes: Uint8Array;
  updatedAt: number;
}

// Files under a prefix; [] if the database doesn't exist. Opening a
// database that does not exist would create an empty one at version 1,
// which IDBFS (version 21) would then refuse to mount; aborting the upgrade
// creates nothing.
export function readIdbfs(db: string, prefix: string): Promise<StoredFile[]> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(db);
    } catch {
      resolve([]);
      return;
    }
    request.onupgradeneeded = () => {
      request.transaction?.abort();
      resolve([]);
    };
    request.onerror = () => resolve([]);
    request.onsuccess = () => {
      const handle = request.result;
      if (!handle.objectStoreNames.contains("FILE_DATA")) {
        handle.close();
        resolve([]);
        return;
      }
      const files: StoredFile[] = [];
      const cursor = handle
        .transaction("FILE_DATA", "readonly")
        .objectStore("FILE_DATA")
        .openCursor(IDBKeyRange.bound(prefix, `${prefix}￿`));
      cursor.onsuccess = () => {
        const c = cursor.result;
        if (!c) {
          handle.close();
          resolve(files);
          return;
        }
        const path = String(c.key);
        const value = c.value as { contents?: Uint8Array; timestamp?: Date | number } | undefined;
        // Directories have no contents.
        if (value?.contents) {
          files.push({
            path,
            name: path.slice(path.lastIndexOf("/") + 1),
            bytes: value.contents,
            updatedAt: new Date(value.timestamp ?? 0).getTime(),
          });
        }
        c.continue();
      };
      cursor.onerror = () => {
        handle.close();
        resolve([]);
      };
    };
  });
}

/** Moves files to new paths in one transaction, skipping any whose target exists. Nothing happens if the database doesn't exist. */
export function moveIdbfs(db: string, moves: [from: string, to: string][]): Promise<void> {
  return new Promise((resolve) => {
    if (!moves.length) {
      resolve();
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(db);
    } catch {
      resolve();
      return;
    }
    request.onupgradeneeded = () => {
      request.transaction?.abort();
      resolve();
    };
    request.onerror = () => resolve();
    request.onsuccess = () => {
      const handle = request.result;
      if (!handle.objectStoreNames.contains("FILE_DATA")) {
        handle.close();
        resolve();
        return;
      }
      const tx = handle.transaction("FILE_DATA", "readwrite");
      const store = tx.objectStore("FILE_DATA");
      for (const [from, to] of moves) {
        const read = store.get(from);
        read.onsuccess = () => {
          if (read.result === undefined) return;
          const taken = store.getKey(to);
          taken.onsuccess = () => {
            if (taken.result !== undefined) return;
            store.put(read.result, to);
            store.delete(from);
          };
        };
      }
      tx.oncomplete = tx.onabort = tx.onerror = () => {
        handle.close();
        resolve();
      };
    };
  });
}

/** Deletes files by path. Nothing happens if the database doesn't exist. */
export function deleteIdbfs(db: string, paths: string[]): Promise<void> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(db);
    } catch {
      resolve();
      return;
    }
    request.onupgradeneeded = () => {
      request.transaction?.abort();
      resolve();
    };
    request.onerror = () => resolve();
    request.onsuccess = () => {
      const handle = request.result;
      if (!handle.objectStoreNames.contains("FILE_DATA")) {
        handle.close();
        resolve();
        return;
      }
      const tx = handle.transaction("FILE_DATA", "readwrite");
      for (const path of paths) tx.objectStore("FILE_DATA").delete(path);
      tx.oncomplete = tx.onabort = tx.onerror = () => {
        handle.close();
        resolve();
      };
    };
  });
}

/**
 * Writes a file as Emscripten's IDBFS stores one, so the core finds it on
 * its next mount. Its directory must already exist. False if the database
 * doesn't exist.
 */
export function writeIdbfs(db: string, path: string, bytes: Uint8Array): Promise<boolean> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(db);
    } catch {
      resolve(false);
      return;
    }
    request.onupgradeneeded = () => {
      request.transaction?.abort();
      resolve(false);
    };
    request.onerror = () => resolve(false);
    request.onsuccess = () => {
      const handle = request.result;
      if (!handle.objectStoreNames.contains("FILE_DATA")) {
        handle.close();
        resolve(false);
        return;
      }
      const tx = handle.transaction("FILE_DATA", "readwrite");
      // A regular file, rw-r--r--.
      tx.objectStore("FILE_DATA").put({ timestamp: new Date(), mode: 0o100644, contents: bytes }, path);
      tx.oncomplete = () => {
        handle.close();
        resolve(true);
      };
      tx.onabort = tx.onerror = () => {
        handle.close();
        resolve(false);
      };
    };
  });
}

/**
 * Writes files into an IDBFS mount's database, creating it as Emscripten
 * would (version 21, FILE_DATA with its timestamp index) when the player has
 * never run in this browser. `dirs` are the directories the files sit in.
 */
export function writeIdbfsFiles(db: string, dirs: string[], files: [path: string, bytes: Uint8Array][]): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(db, 21);
    request.onupgradeneeded = () => {
      const handle = request.result;
      if (!handle.objectStoreNames.contains("FILE_DATA")) {
        handle.createObjectStore("FILE_DATA").createIndex("timestamp", "timestamp", { unique: false });
      }
    };
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const handle = request.result;
      const tx = handle.transaction("FILE_DATA", "readwrite");
      const store = tx.objectStore("FILE_DATA");
      const now = new Date();
      // A directory, rwxr-xr-x; a regular file, rw-r--r--.
      for (const dir of dirs) store.put({ timestamp: now, mode: 0o40755 }, dir);
      for (const [path, bytes] of files) store.put({ timestamp: now, mode: 0o100644, contents: bytes }, path);
      tx.oncomplete = () => {
        handle.close();
        resolve();
      };
      tx.onabort = tx.onerror = () => {
        handle.close();
        reject(tx.error);
      };
    };
  });
}
